import { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { UploadedDateFilter } from '~/components/Filters/DateFilter/UploadedDateFilter';
import { SingleFilterContainer } from '~/components/Filters/SingleFilters/SingleFilterContainer';
import { type SingleFilterProps } from '~/components/Filters/types';
import {
  setUploadedDateFilterEndDateAction,
  setUploadedDateFilterStartDateAction,
  setUploadedDateFilterTypeAction,
} from '~/store/filters/actions';
import {
  uploadedDateFilterDateTypeSelector,
  uploadedDateFilterEndDateSelector,
  uploadedDateFilterStartDateSelector,
} from '~/store/filters/selectors';
import { type DateRangeType } from '~/store/filters/types';

export const SingleUploadedDateFilter = memo(({ onApplyClick }: SingleFilterProps) => {
  const dispatch = useDispatch();
  const dateType = useSelector(uploadedDateFilterDateTypeSelector);
  const startDate = useSelector(uploadedDateFilterStartDateSelector);
  const endDate = useSelector(uploadedDateFilterEndDateSelector);

  const onDateTypeChange = useCallback(
    (type: DateRangeType) => {
      dispatch(setUploadedDateFilterTypeAction({ type }));
    },
    [dispatch],
  );

  const onStartDateChange = useCallback(
    (date: Date | [Date, Date] | null) => {
      if (Array.isArray(date)) return;
      dispatch(setUploadedDateFilterStartDateAction({ startDate: date }));
      onApplyClick();
    },
    [dispatch, onApplyClick],
  );

  const onEndDateChange = useCallback(
    (date: Date | [Date, Date] | null) => {
      if (Array.isArray(date)) return;
      dispatch(setUploadedDateFilterEndDateAction({ endDate: date }));
      onApplyClick();
    },
    [dispatch, onApplyClick],
  );

  return (
    <SingleFilterContainer currentFilter="dateUploaded" onApplyClick={onApplyClick}>
      <UploadedDateFilter
        startDate={startDate}
        endDate={endDate}
        dateType={dateType}
        onStartDateChange={onStartDateChange}
        onEndDateChange={onEndDateChange}
        onTypeChange={onDateTypeChange}
      />
    </SingleFilterContainer>
  );
});

SingleUploadedDateFilter.displayName = 'SingleUploadedDateFilter';
